import * as tf from '@tensorflow/tfjs';
import * as mobilenet from '@tensorflow-models/mobilenet';
import * as knnClassifier from '@tensorflow-models/knn-classifier';
import mlDb from '../../ml/db';
import formatMessage from 'format-message';

class MachineLearningExtension {
    constructor (runtime) {
        this.runtime = runtime;

        // Model state
        this.classifier = knnClassifier.create();
        this.mobilenetModule = null;
        this.activeProject = null;
        this.isLoaded = false;
        
        // Inference state
        this.currentPrediction = null;
        this.confidences = {};

        // Video state
        this._video = null;
        this._videoReady = false;

        if (this.runtime) {
            this.runtime.on('PROJECT_STOP_ALL', () => {
                this._stopVideo();
            });
        }
        
        // Trigger model loading immediately upon extension initialization
        this._loadModelInit();
    }

    async _loadModelInit() {
        if (this.runtime) {
            this.runtime.emit('MODEL_LOADING', 'Machine Learning');
        }
        await this._loadActiveModel();
        if (this.runtime) {
            this.runtime.emit('MODEL_LOADED', 'Machine Learning');
        }
    }

    _stopVideo () {
        if (this._video && this._video.srcObject) {
            const tracks = this._video.srcObject.getTracks();
            tracks.forEach(track => track.stop());
            this._video.srcObject = null;
        }
        this._videoReady = false;
    }

    getInfo () {
        return {
            id: 'mlClassifier',
            name: formatMessage({
                id: 'gui.extension.mlClassifier.name',
                default: 'My Trained Model'
            }),
            color1: '#6842c2',
            color2: '#53349c',
            blocks: [
                {
                    opcode: 'analyseImage',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'mlClassifier.blocks.analyseImage',
                        default: 'analyse image from camera'
                    })
                },
                {
                    opcode: 'getPredictedClass',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'mlClassifier.blocks.getPredictedClass',
                        default: 'get predicted class'
                    })
                },
                {
                    opcode: 'getConfidence',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'mlClassifier.blocks.getConfidence',
                        default: 'confidence of [CLASS_NAME] %'
                    }),
                    arguments: {
                        CLASS_NAME: {
                            type: 'string',
                            menu: 'classMenu',
                            defaultValue: 'Class 1'
                        }
                    }
                },
                {
                    opcode: 'isClassDetected',
                    blockType: 'Boolean',
                    text: formatMessage({
                        id: 'mlClassifier.blocks.isClassDetected',
                        default: 'is [CLASS_NAME] detected?'
                    }),
                    arguments: {
                        CLASS_NAME: {
                            type: 'string',
                            menu: 'classMenu',
                            defaultValue: 'Class 1'
                        }
                    }
                }
            ],
            menus: {
                classMenu: {
                    acceptReporters: true,
                    items: '_getClassMenu'
                }
            }
        };
    }

    _getClassMenu () {
        if (!this.activeProject || !this.activeProject.classes || this.activeProject.classes.length === 0) {
            return ['Class 1', 'Class 2'];
        }
        return this.activeProject.classes.map(c => ({
            text: c.name,
            value: c.name
        }));
    }

    async _ensureVideo () {
        if (this._video && this._videoReady) return this._video;
        if (!this._video) {
            this._video = document.createElement('video');
            this._video.setAttribute('autoplay', '');
            this._video.setAttribute('playsinline', '');
            this._video.width = 480;
            this._video.height = 360;
            this._video.style.display = 'none';
            document.body.appendChild(this._video);
        }
        if (!this._videoReady) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {width: 480, height: 360}
                });
                this._video.srcObject = stream;
                await this._video.play();
                this._videoReady = true;
            } catch (err) {
                console.warn('[ML Classifier] Camera access denied:', err);
            }
        }
        return this._video;
    }

    async _loadActiveModel () {
        if (this.isLoaded) return;
        
        await tf.ready();

        if (!this.mobilenetModule) {
            this.mobilenetModule = await mobilenet.load({version: 2, alpha: 1.0});
        }

        const projects = await mlDb.getAllProjects();
        if (projects.length > 0) {
            this.activeProject = projects[0]; // Load the most recently saved project
            
            if (this.activeProject.modelData) {
                this.classifier.clearAllClasses();
                const datasetObj = this.activeProject.modelData;
                const tensorObj = {};
                Object.keys(datasetObj).forEach(key => {
                    const data = datasetObj[key].data;
                    const shape = datasetObj[key].shape;
                    tensorObj[key] = tf.tensor(data, shape);
                });
                this.classifier.setClassifierDataset(tensorObj);
            }
        }
        this.isLoaded = true;
        console.log('[ML Classifier] Model successfully loaded into Scratch!');
    }

    async analyseImage () {
        await this._loadActiveModel();
        
        const video = await this._ensureVideo();
        if (!video || !this._videoReady || !this.mobilenetModule || this.classifier.getNumClasses() === 0) {
            return;
        }

        try {
            const img = tf.browser.fromPixels(video);
            const activation = this.mobilenetModule.infer(img, 'conv_preds');
            const result = await this.classifier.predictClass(activation);
            
            let className = result.label;
            if (this.activeProject && this.activeProject.classes) {
                const classDef = this.activeProject.classes.find(c => c.id === Number(result.label));
                if (classDef) className = classDef.name;
            }

            this.currentPrediction = className;
            this.confidences = {};
            
            Object.keys(result.confidences).forEach(labelId => {
                let name = labelId;
                if (this.activeProject && this.activeProject.classes) {
                    const cDef = this.activeProject.classes.find(c => c.id === Number(labelId));
                    if (cDef) name = cDef.name;
                }
                this.confidences[name] = result.confidences[labelId] * 100;
            });

            img.dispose();
            activation.dispose();
        } catch (e) {
            console.error('[ML Classifier] Error during analysis:', e);
        }
    }

    getPredictedClass () {
        return this.currentPrediction || '';
    }

    getConfidence (args) {
        const className = args.CLASS_NAME || '';
        return Math.round(this.confidences[className] || 0);
    }

    isClassDetected (args) {
        const className = args.CLASS_NAME || '';
        return this.currentPrediction === className;
    }
}

export default MachineLearningExtension;
